import 'react-toastify/dist/ReactToastify.min.css';

import { CssBaseline, styled, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ConfirmProvider } from 'material-ui-confirm';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { defaultConfirmProps, Locales } from './config/constants';
import { DateFnsProvider } from './contexts/DateFnsProvider';
import { IntlContextProvider, IntlState } from './contexts/IntlContext';
import { OidcContextProvider } from './contexts/OidcContext/OidcContextProvider';
import { OidcWrapper } from './contexts/OidcContext/OidcWrapper';
import { UiContextProvider } from './contexts/UiContext';
import { theme } from './theme';
import en from './translations/en.json';
import fr from './translations/fr.json';
import { App } from './views/App';

const messagesMap: Record<Locales, Record<string, any>> = {
    [Locales.fr]: fr,
    [Locales.en]: en,
};
const intlConfig: Partial<IntlState> = {
    translations: messagesMap,
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // avoid retry when 404 or already retried twice
            retry: (retryCount, error) => retryCount < 3 && ![404, 403].includes((error as any)?.response?.status),
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchInterval: false,
            refetchOnMount: false,
        },
    },
});

const StyledToastContainer = styled(ToastContainer)`
    min-width: 320px;
    width: auto;
`;

export const Root = () => {
    return (
        <BrowserRouter>
            <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools initialIsOpen={false} />
                <OidcContextProvider>
                    <OidcWrapper>
                        <IntlContextProvider config={intlConfig} textComponent="span">
                            <DateFnsProvider>
                                <ThemeProvider theme={theme}>
                                    <UiContextProvider>
                                        <ConfirmProvider defaultOptions={defaultConfirmProps}>
                                            <CssBaseline />
                                            <App />
                                            <StyledToastContainer />
                                        </ConfirmProvider>
                                    </UiContextProvider>
                                </ThemeProvider>
                            </DateFnsProvider>
                        </IntlContextProvider>
                    </OidcWrapper>
                </OidcContextProvider>
            </QueryClientProvider>
        </BrowserRouter>
    );
};
